import { Alert, AlertTitle, Box, Button, Checkbox, FormControlLabel, Typography } from '@mui/material';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Field, FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';

import api from '../../../redux/features/auth/axiosAuthHelper';
import { TextEditField } from '../fields/fields';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import colorConfigs from '../../../configs/colorConfigs';
import { DeviceSessionStorageKey, DeviceTableParameterStorageService } from '../../../services/sessionStorage';
import RareButton from '../buttons/RareButton';
import { Device } from '../types';
import StatusChip from '../StatusChip';

interface DeviceDetailProps {
    device_id?: number | null;
    handleRefreshButton: () => void;
    handleBackButton: () => void;
    onStatusChanged?: () => void;
}

const DeviceDetailCard: React.FC<DeviceDetailProps> = ({ device_id, handleRefreshButton, handleBackButton, onStatusChanged }) => {
    const authData = useSelector((state: RootState) => state.auth);
    const [errors, setErrors] = useState<string | null>(null);
    const [device, setDevice] = useState<Device>();
    const [activeStatus, setActiveStatus] = useState<boolean | null>(null);
    const [statusBusy, setStatusBusy] = useState(false);
    const create = !device_id;
    const currentDeviceId = useRef(device_id);
    const submittedDeviceId = useRef<number | null | undefined>(undefined);

    useLayoutEffect(() => {
        currentDeviceId.current = device_id;
        setDevice(undefined);
        setActiveStatus(null);
        setErrors(null);
        return () => { currentDeviceId.current = null; };
    }, [device_id]);

    useEffect(() => {
        if (!device_id) return;
        let current = true;
        const fetchDevice = async () => {
            try {
                const response = await api.get(`devices/${device_id}/`, {
                    headers: { Authorization: `Bearer ${authData.access}` },
                });
                if (!current) return;
                const fetchedDevice = response.data as Device;
                setDevice(fetchedDevice);
                setActiveStatus(fetchedDevice.is_active);
            } catch (error) {
                if (!current) return;
                console.error('Error fetching phone configuration', error);
                setErrors(`No Phone Configuration found with ID ${device_id}`);
            }
        };
        void fetchDevice();
        return () => { current = false; };
    }, [device_id, authData.access]);

    const formik = useFormik({
        enableReinitialize: !create,
        validateOnChange: false,
        validateOnBlur: false,
        initialValues: device ? {
            id: device.id,
            name: device.name,
            model: device.model,
            h_factor: device.h_factor,
            ultra_wide: device.ultra_wide,
        } : {
            id: 0,
            name: '',
            model: '',
            h_factor: 0,
            ultra_wide: false,
        },
        validationSchema: Yup.object().shape({}),
        onSubmit: async (values) => {
            if (!authData.isPortalUser || (!create && (!device || device.id !== device_id || values.id !== device.id || submittedDeviceId.current !== device_id))) return;
            const data = {
                name: values.name,
                model: values.model,
                h_factor: values.h_factor,
                ultra_wide: values.ultra_wide,
                ...(create ? { is_active: true } : {}),
            };
            const requestDeviceId = device_id;
            try {
                await api({
                    method: create ? 'post' : 'patch',
                    url: create ? 'devices/' : `devices/${requestDeviceId}/`,
                    data,
                    headers: { Authorization: `Bearer ${authData.access}` },
                });
                if (currentDeviceId.current !== requestDeviceId) return;
                setErrors(null);
                handleRefreshButton();
                const count = DeviceTableParameterStorageService.get(DeviceSessionStorageKey.count);
                DeviceTableParameterStorageService.set(DeviceSessionStorageKey.count, count + 1);
            } catch (error: any) {
                if (currentDeviceId.current !== requestDeviceId) return;
                const response = error?.response;
                if (response?.status === 400 && response.data && typeof response.data === 'object') {
                    const field = Object.keys(response.data)[0];
                    setErrors(`${field}: ${response.data[field]}`);
                } else if (response?.status === 403) {
                    setErrors('You do not have permission to save this phone configuration.');
                } else {
                    setErrors('The phone configuration could not be saved. Please try again.');
                }
            }
        },
    });

    const formReady = create || (!!device && device.id === device_id && formik.values.id === device.id);
    const fieldsDisabled = !authData.isPortalUser || !formReady;

    const submitForm = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!formReady || statusBusy || formik.isSubmitting || !authData.isPortalUser) return;
        submittedDeviceId.current = device_id;
        formik.handleSubmit(event);
    };

    const changeStatus = async () => {
        if (!device || !formReady || activeStatus === null || statusBusy || !authData.isPortalUser) return;
        const requestDeviceId = device.id;
        const nextStatus = !activeStatus;
        setStatusBusy(true);
        setErrors(null);
        try {
            const response = await api({
                method: 'patch',
                url: `devices/${device.id}/`,
                data: { is_active: nextStatus },
                headers: { Authorization: `Bearer ${authData.access}` },
            });
            if (response.data?.is_active !== nextStatus) throw new Error('Unexpected phone configuration status response');
            if (currentDeviceId.current === requestDeviceId) setActiveStatus(nextStatus);
            onStatusChanged?.();
        } catch (error: any) {
            const detail = error?.response?.data?.is_active;
            if (currentDeviceId.current === requestDeviceId) {
                setErrors(Array.isArray(detail) ? detail.join(' ') :
                    typeof detail === 'string' ? detail :
                        'The phone configuration status could not be changed. Please try again.');
            }
        } finally {
            setStatusBusy(false);
        }
    };

    return (
        <FormikProvider value={formik}>
            <form aria-label="Phone configuration" onSubmit={submitForm}>
                <Box sx={{ px: { xs: 1, sm: 2 }, pt: 1, pb: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    {errors && <Alert severity="error"><AlertTitle>Error</AlertTitle>{errors}</Alert>}

                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                        <Field name="name" type="text" label="Device Name" component={TextEditField} disabled={fieldsDisabled} />
                        <Field name="model" type="text" label="Device Model" component={TextEditField} disabled={fieldsDisabled} />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', gap: 2 }}>
                        <Field name="h_factor" type="number" label="H Factor" component={TextEditField} disabled={fieldsDisabled} />
                        <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <FormControlLabel
                                control={<Checkbox checked={formik.values.ultra_wide} name="ultra_wide"
                                    onChange={formik.handleChange} disabled={fieldsDisabled}
                                    sx={{ '&.Mui-checked': { color: colorConfigs.buttons.active } }} />}
                                label="Ultra Wide"
                                sx={{ m: 0 }}
                            />
                        </Box>
                    </Box>

                    {!create && device && <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 2, borderTop: '1px solid #e1e8e6', pt: 2 }}>
                        <Box>
                            <Typography variant="caption" display="block" sx={{ color: '#58646a' }}>Phone Configuration ID</Typography>
                            <Typography variant="body2" sx={{ color: colorConfigs.brand.logoText }}>{device.id}</Typography>
                        </Box>
                        <Box sx={{ justifySelf: 'end', alignSelf: 'end' }}>
                            <StatusChip enabled={activeStatus ?? false} trueLabel="Active" falseLabel="Inactive" />
                        </Box>
                    </Box>}

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end', gap: 1, borderTop: '1px solid #e1e8e6', pt: 1.5 }}>
                        {!create && device && authData.isPortalUser && <Button
                            type="button"
                            disabled={!formReady || statusBusy || formik.isSubmitting || activeStatus === null}
                            onClick={() => void changeStatus()}
                            sx={{
                                mr: 'auto', textTransform: 'none', fontWeight: 700,
                                color: activeStatus ? '#b3261e' : colorConfigs.buttons.active,
                                backgroundColor: activeStatus ? '#fcf4f3' : '#f1f8f2',
                                '&:hover': { backgroundColor: activeStatus ? '#fbe9e7' : '#e5f3e8' },
                            }}
                        >
                            {statusBusy ? 'Updating status…' : activeStatus ? 'Deactivate phone configuration' : 'Reactivate phone configuration'}
                        </Button>}
                        <RareButton type="button" onClick={handleBackButton} variant="outlined" sx={{ m: 1 }}>Cancel</RareButton>
                        {authData.isPortalUser && <RareButton type="submit" variant="success"
                            disabled={(!create && (!formik.dirty || !formReady)) || statusBusy || formik.isSubmitting} sx={{ m: 1 }}>
                            {formik.isSubmitting ? (create ? 'Creating…' : 'Saving…') : create ? 'Create Phone Configuration' : 'Save Changes'}
                        </RareButton>}
                    </Box>
                </Box>
            </form>
        </FormikProvider>
    );
};

export default DeviceDetailCard;
